import type {CreateExtArgs, DAL, Extension, ExtensionAPI} from '@wix/document-manager-core'
import type {
    Pointer,
    Pointers,
    WixCodeConnectionItem,
    IConnectionItem,
    ConnectionItem,
    Connection,
    ControllerConnectionItem,
    CompRef,
    ConnectionList
} from '@wix/document-services-types'
import _ from 'lodash'
import type {ComponentsMetadataAPI} from './componentsMetadata/componentsMetadata'
import {getComponentIncludingDisplayOnly, getComponentType} from '../utils/dalUtils'
import {COMP_TYPES, DATA_TYPES, MASTER_PAGE_ID, VIEW_MODES} from '../constants/constants'
import {deepClone} from '@wix/wix-immutable-proxy'
import type {DataModelExtensionAPI} from './dataModel/dataModel'
import {isRefPointer} from '../utils/refStructureUtils'
import type {ControllersExtensionAPI} from './controllers'
import type {TPAExtensionAPI} from './tpa'
import type {RelationshipsAPI} from './relationships'

const INVALID_ROLE = '*'

export type ValidateConnection = (
    connectionItem: ConnectionItemForValidation,
    otherConnectionItems: ConnectionItemForValidation[],
    componentRef: Pointer,
    controllerRef: Pointer | CompRef
) => void

export type GetConnections = (compRef: Pointer) => IConnectionItem[] | null
export type GetResolvedConnections = (compRef: Pointer) => Record<string, any>[] | null
export type GetConnectedComponentsForController = (controllerPointer: Pointer) => CompRef[]

export type GetConnectionsByDataItem = (dataItemId: string, pageId: string) => IConnectionItem[] | null
export type GetResolvedConnectionsByDataItem = (
    dataItemId: string,
    pagePointer: Pointer,
    viewMode: string
) => Record<string, any>[] | null
export type UpdateConnectionsItem = (componentPointer: Pointer, connectionsItem: IConnectionItem[]) => string
export type UpdateConnectionsItemByDataItem = (
    connectionItemId: string,
    pageId: string,
    connectionsItem: IConnectionItem[]
) => string
export type CreateWixCodeConnectionItem = (nickname: string) => WixCodeConnectionItem
export type CreateConnectionItem = (
    role: string,
    controllerId: string,
    isPrimary?: boolean,
    subRole?: string
) => ControllerConnectionItem
export interface ConnectionItemForValidation {
    isPrimary: boolean
    role: string
}

export interface ConnectionsAPI extends ExtensionAPI {
    connections: {
        connect(
            compRef: Pointer,
            controllerRef: Pointer | CompRef,
            role: string,
            connectionConfig?: any,
            isPrimary?: boolean,
            subRole?: string
        ): void
        disconnect(compPointer: CompRef, connection: Pointer): void
        validateConnection: ValidateConnection
        get: GetConnections
        getResolved: GetResolvedConnections
        getResolvedConnectionsByDataItem: GetResolvedConnectionsByDataItem
        getConnectionsByDataItem: GetConnectionsByDataItem
        getConnectedComponentsForController: GetConnectedComponentsForController
        updateConnectionsItem: UpdateConnectionsItem
        updateConnectionsItemByDataItem: UpdateConnectionsItemByDataItem
        createWixCodeConnectionItem: CreateWixCodeConnectionItem
        createConnectionItem: CreateConnectionItem
        serializeConnectionsItem(connectionsItem: IConnectionItem[]): IConnectionItem[]
    }
}

export const EVENTS = {
    CONNECTIONS: {
        UPDATE_AFTER: 'connection_update_after'
    }
}

const createExtension = (): Extension => {
    const validateControllerComponent = (
        dal: DAL,
        pointers: Pointers,
        extensionAPI: ExtensionAPI,
        controllerRef: CompRef,
        componentRef: CompRef
    ): void => {
        const {tpa, dataModel} = extensionAPI as TPAExtensionAPI & DataModelExtensionAPI
        const isOOIController = (compType: string) => {
            return tpa.isTpaByCompType(compType)
        }
        const controllerQuery = dataModel.components.getItem(controllerRef, DATA_TYPES.controllers)
        if (controllerQuery) {
            throw new Error('cannot connect to a component with controller list, provide specific controller')
        }
        const controllerCompType = getComponentIncludingDisplayOnly(dal, controllerRef).componentType
        if (controllerCompType === COMP_TYPES.APP_WIDGET_TYPE) {
            if (!pointers.structure.isDescendant(componentRef, controllerRef)) {
                throw new Error('cannot connect to app widget from the outside')
            }
        } else if (controllerCompType !== COMP_TYPES.CONTROLLER_TYPE && !isOOIController(controllerCompType)) {
            throw new Error('controllerRef component type is invalid - should be a controller or current context')
        }
    }

    const validateConnection = (
        dal: DAL,
        pointers: Pointers,
        extensionAPI: ExtensionAPI,
        componentsMetaData: ComponentsMetadataAPI,
        connectionItem: ConnectionItemForValidation,
        otherConnectionItems: ConnectionItemForValidation[],
        componentRef: Pointer,
        controllerRef: Pointer
    ): void => {
        const {controllers} = extensionAPI as ControllersExtensionAPI

        const isComponentRef = _.includes(VIEW_MODES, controllerRef.type)
        const connectedComponentType = getComponentIncludingDisplayOnly(dal, componentRef).componentType

        if (isComponentRef) {
            validateControllerComponent(dal, pointers, extensionAPI, controllerRef as CompRef, componentRef as CompRef)
        }

        if (!isComponentRef && controllerRef.type !== DATA_TYPES.data) {
            throw new Error('scoped controllerRef should be a data pointer')
        }

        if (isRefPointer(controllerRef)) {
            throw new Error('controllerRef component cannot be inflated component')
        }

        if (connectionItem.isPrimary && connectedComponentType === COMP_TYPES.CONTROLLER_TYPE) {
            throw new Error('cannot connect to another app controller with a primary connection')
        }

        if (connectionItem.role === INVALID_ROLE) {
            throw new Error('invalid connection role - cannot be *')
        }

        if (!componentsMetaData.componentsMetadata.canConnectToCode(componentRef)) {
            throw new Error('cannot connect to this component type')
        }

        if (connectionItem.isPrimary && _.some(otherConnectionItems, {isPrimary: true})) {
            throw new Error('Primary connection is already connected to the component')
        }

        if (!isComponentRef && controllerRef.type === DATA_TYPES.data) {
            const availableScopedControllers = controllers.getAvailableScopedControllerRefs(componentRef as CompRef)
            if (!availableScopedControllers.find((c: Pointer) => c.id === controllerRef.id)) {
                throw new Error('Controller is not available for this component to connect to')
            }
        }
    }

    const createExtensionAPI = ({dal, pointers, extensionAPI, eventEmitter}: CreateExtArgs): ConnectionsAPI => {
        const componentsMetaData = extensionAPI as ComponentsMetadataAPI
        const getControllerRefFromId = (controllerDataId: string, pagePointer: Pointer, viewMode: string) => {
            const {relationships} = extensionAPI as RelationshipsAPI
            const dataPointer = pointers.data.getDataItem(controllerDataId, pagePointer.id)
            let owningReferences = relationships.getOwningReferencesToPointer(dataPointer, DATA_TYPES.controllers)
            // in case of scoped controllers we are using the data item itself as the controller ref
            if (owningReferences.length > 0) {
                return dataPointer
            }

            owningReferences = relationships.getOwningReferencesToPointer(dataPointer, viewMode)

            if (owningReferences.length === 0 && viewMode === VIEW_MODES.MOBILE) {
                owningReferences = relationships.getOwningReferencesToPointer(dataPointer, VIEW_MODES.DESKTOP)
            }
            if (owningReferences.length > 0) {
                const component = owningReferences[0]
                const pageOfComponent = pointers.structure.getPageOfComponent(component)

                if (pageOfComponent.id === pagePointer.id) {
                    return component
                }

                if (pointers.structure.isMasterPage(pagePointer)) {
                    return null
                }
                if (pageOfComponent.id === MASTER_PAGE_ID) {
                    return component
                }
            }

            return null
        }

        const resolveConnectionItems = (connectionsItems: ConnectionItem[], pagePointer: Pointer, viewMode: string) =>
            _.map(connectionsItems, connectionItem => {
                if (connectionItem.type === 'WixCodeConnectionItem') {
                    return connectionItem
                }

                const controllerRef = getControllerRefFromId(connectionItem.controllerId, pagePointer, viewMode)
                const newConnectionItem: ControllerConnectionItem = _.assign(
                    {},
                    _.omit(connectionItem, 'controllerId'),
                    {controllerRef}
                ) as unknown as ControllerConnectionItem
                if (!_.has(newConnectionItem, ['config'])) {
                    return newConnectionItem
                }
                newConnectionItem.config = JSON.parse(newConnectionItem.config!)
                return newConnectionItem
            })

        const getConnections = (componentPointer: Pointer): ConnectionItem[] => {
            const {dataModel} = extensionAPI as DataModelExtensionAPI
            const connectionsData = dataModel.components.getItem(componentPointer, DATA_TYPES.connections)
            return deepClone(_.get(connectionsData, ['items']) ?? [])
        }
        const getResolvedConnections = (componentPointer: Pointer): IConnectionItem[] => {
            const connectionsItems = getConnections(componentPointer)

            const pagePointer = pointers.structure.getPageOfComponent(componentPointer)

            return resolveConnectionItems(connectionsItems, pagePointer, componentPointer.type)
        }

        const getConnectionsByDataItem = (connectionDataItemId: string, pageId: string) => {
            const {dataModel} = extensionAPI as DataModelExtensionAPI
            const connectionsData = dataModel.getItem(connectionDataItemId, DATA_TYPES.connections, pageId)
            const connectionsItems = connectionsData?.items
            if (_.isEmpty(connectionsItems)) {
                return []
            }
            return connectionsItems
        }

        const getResolvedConnectionsByDataItem = (
            connectionDataItemId: string,
            pagePointer: Pointer,
            viewMode: string
        ) => {
            const connections = getConnectionsByDataItem(connectionDataItemId, pagePointer.id)

            return resolveConnectionItems(connections, pagePointer, viewMode)
        }

        const serializeConnectionsItem = (connectionsItems: (IConnectionItem | Connection)[]): IConnectionItem[] => {
            const hasControllerRef = (item: IConnectionItem | Connection): item is Connection => {
                return 'controllerRef' in item
            }

            return _.map(connectionsItems, function (connectionItem) {
                if (connectionItem.type === 'WixCodeConnectionItem') {
                    return connectionItem
                }

                const {dataModel} = extensionAPI as DataModelExtensionAPI
                let newConnectionItem: unknown = connectionItem

                if (hasControllerRef(connectionItem) && connectionItem.controllerRef) {
                    const isDataPointer = connectionItem.controllerRef.type === DATA_TYPES.data
                    let controllerDataItemId

                    if (isDataPointer) {
                        controllerDataItemId = (connectionItem as Connection).controllerRef.id
                    } else {
                        // if got legacy component reference, we need to get the data item id from the component
                        controllerDataItemId = dataModel.components.getComponentDataItemId(
                            (connectionItem as Connection).controllerRef,
                            DATA_TYPES.data
                        )
                    }

                    newConnectionItem = _.assign({}, _.omit(connectionItem, 'controllerRef'), {
                        controllerId: controllerDataItemId
                    }) as ControllerConnectionItem
                }

                if (!_.has(newConnectionItem, ['config'])) {
                    return newConnectionItem
                }

                try {
                    if (_.isObject(_.get(newConnectionItem, ['config']))) {
                        ;(newConnectionItem as ControllerConnectionItem).config = JSON.stringify(
                            (newConnectionItem as ControllerConnectionItem).config
                        )
                    }
                } catch (e) {
                    throw new Error('Invalid connection configuration - should be JSON stringifiable')
                }
                return newConnectionItem
            }) as IConnectionItem[]
        }

        const updateConnectionsItem = (componentPointer: Pointer, connectionsItem: IConnectionItem[]): string => {
            connectionsItem = serializeConnectionsItem(connectionsItem)
            const {dataModel} = extensionAPI as DataModelExtensionAPI
            let connectionsId = dataModel.components.getComponentDataItemId(componentPointer, 'connections')
            const pageId = pointers.structure.getPageOfComponent(componentPointer).id

            const doesComponentHaveConnectionsData = Boolean(connectionsId)
            const itemToUpdate = dataModel.createConnectionsItem(connectionsItem)
            const connectionsPointer = dataModel.addItem(itemToUpdate, DATA_TYPES.connections, pageId, connectionsId)
            connectionsId = connectionsPointer.id
            if (!doesComponentHaveConnectionsData) {
                dataModel.components.linkComponentToItemByTypeDesktopAndMobile(
                    componentPointer,
                    connectionsId,
                    DATA_TYPES.connections
                )
            }
            eventEmitter.emit(EVENTS.CONNECTIONS.UPDATE_AFTER, itemToUpdate, componentPointer)
            return connectionsId
        }

        const updateConnectionsItemByDataItem = (
            connectionDataItemId: string,
            pageId: string,
            connectionsItem: IConnectionItem[]
        ): string => {
            const {dataModel} = extensionAPI as DataModelExtensionAPI
            const itemToUpdate = dataModel.createConnectionsItem(connectionsItem)
            const connectionsPointer = dataModel.addItem(
                itemToUpdate,
                DATA_TYPES.connections,
                pageId,
                connectionDataItemId
            )
            return connectionsPointer.id
        }

        const createWixCodeConnectionItem = (nickname: string): WixCodeConnectionItem => {
            return {
                type: 'WixCodeConnectionItem',
                role: nickname
            }
        }
        const createConnectionItem = (
            role: string,
            controllerId: string,
            isPrimary: boolean = true,
            subRole?: string
        ): ControllerConnectionItem => {
            return _.assign(
                {
                    type: 'ConnectionItem',
                    role,
                    controllerId,
                    isPrimary
                },
                subRole ? {subRole} : {}
            ) as ControllerConnectionItem
        }
        const isAppWidgetType = (compType: string) => compType === COMP_TYPES.APP_WIDGET_TYPE

        const connect = (
            compRef: Pointer,
            controllerRef: Pointer | CompRef,
            role: string,
            connectionConfig?: any,
            isPrimary?: boolean,
            subRole?: string
        ) => {
            const controllerCompType = getComponentType(dal, controllerRef)
            isPrimary = isAppWidgetType(controllerCompType) && isPrimary !== false ? true : isPrimary || false

            const newConnectionItem: any = {
                type: 'ConnectionItem',
                controllerRef,
                role,
                isPrimary
            }
            if (connectionConfig) {
                newConnectionItem.config = connectionConfig
            }
            if (subRole) {
                newConnectionItem.subRole = subRole
            }
            // The controllerRef might be null when reconnecting a connection to the page itself (rather than to another comp).
            // For example, if a page has a background connected to a dataset, then during the duplication of that page the
            // controllerRef might be null. This has to do with the order in which the components are added to the new page.
            // We reject connections with null controllerRefs to avoid issues in these scenarios
            const existingConnections: IConnectionItem[] = getResolvedConnections(compRef)
            const otherConnections = _(existingConnections)
                .reject({controllerRef: null} as any)
                .reject({controllerRef, role} as any)
                .value()

            validateConnection(
                dal,
                pointers,
                extensionAPI,
                componentsMetaData,
                newConnectionItem,
                otherConnections as unknown as ConnectionItemForValidation[],
                compRef,
                controllerRef
            )

            updateConnectionsItem(compRef, otherConnections.concat(newConnectionItem))
        }

        /**
         * Disconnects a component from a controller by removing the specified connection.
         * @param compRef - The component reference to disconnect from
         * @param controllerRef - The controller pointer to disconnect
         * @param role - Optional role filter to disconnect only specific connection types
         */
        const disconnect = (compRef: Pointer, controllerRef: Pointer, role?: string): void => {
            const {dataModel} = extensionAPI as DataModelExtensionAPI
            const existingConnections: ConnectionList | undefined = dataModel.components.getItem(
                compRef,
                DATA_TYPES.connections
            )
            if (!existingConnections?.items) {
                return
            }

            const filter: Partial<ControllerConnectionItem> = {controllerId: controllerRef.id}
            if (role) {
                filter.role = role
            }
            const newConnections = _.reject(existingConnections.items, filter)
            if (_.isEmpty(newConnections)) {
                dataModel.components.removeItem(compRef, DATA_TYPES.connections)
            } else {
                existingConnections.items = newConnections
                dataModel.components.addItem(compRef, DATA_TYPES.connections, existingConnections)
            }
        }

        /**
         * Gets all components connected to a specific controller.
         * @param controllerPointer - The controller pointer to get connections for
         * @returns Array of component references connected to the controller
         */
        const getConnectedComponentsForController = (controllerPointer: Pointer): CompRef[] => {
            const {relationships} = extensionAPI as RelationshipsAPI
            return relationships
                .getReferencesToPointer(controllerPointer, DATA_TYPES.connections)
                .flatMap(connection => relationships.getOwningComponentsToPointer(connection) as CompRef[])
        }

        return {
            connections: {
                connect,
                disconnect,
                get: getConnections,
                getResolved: getResolvedConnections,
                getConnectionsByDataItem,
                getResolvedConnectionsByDataItem,
                getConnectedComponentsForController,
                updateConnectionsItem,
                updateConnectionsItemByDataItem,
                createWixCodeConnectionItem,
                createConnectionItem,
                serializeConnectionsItem,
                validateConnection: (
                    connectionItem: ConnectionItemForValidation,
                    otherConnectionItems: ConnectionItemForValidation[],
                    componentRef: Pointer,
                    controllerRef: Pointer
                ): void =>
                    validateConnection(
                        dal,
                        pointers,
                        extensionAPI,
                        componentsMetaData,
                        connectionItem,
                        otherConnectionItems,
                        componentRef,
                        controllerRef
                    )
            }
        }
    }

    return {
        name: 'connections',
        EVENTS,
        dependencies: new Set(['structure', 'tpa', 'componentsMetadata', 'platformWithViewer', 'controllers']),
        createExtensionAPI
    }
}

export {createExtension}
